import * as React from 'react';

import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { MappingField } from 'src/containers/ProjectDiagram/types/data';
import { isResource } from 'src/containers/ProjectDiagram/utils/data';
import { entityDrawerNodeDataSelector } from 'src/selectors/entityDrawerNodeData';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { Connection } from 'src/store/connections/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useAddResourceMapping = (connection: Connection | null) => {
  const isAutoMappingMultipleTablesSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isAutoMappingMultipleTablesSupported',
      connection
    )
  );

  const handleNewAddMapping = useNewAddResourceMapping();
  const handleOldAddMapping = useOldAddResourceMapping();

  return isAutoMappingMultipleTablesSupported
    ? handleNewAddMapping
    : handleOldAddMapping;
};

type DialogOptions = Omit<
  Parameters<typeof uiSlice.actions.toggledResourceSuggestionsDialog>[0],
  'isOpen'
>;

export const useNewAddResourceMapping = () => {
  const dispatch = useReduxDispatch();

  return React.useCallback(
    (options: DialogOptions = {}) => {
      dispatch(
        uiSlice.actions.toggledResourceSuggestionsDialog({
          ...options,
          isOpen: true,
        })
      );
    },
    [dispatch]
  );
};

export const useOldAddResourceMapping = () => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  // entityDrawerNodeDataSelector is generic with a type-guard arg, so it can't
  // be memoized at the source; memoize the instance here instead.
  const nodeDataSelector = React.useMemo(
    () => entityDrawerNodeDataSelector(projectId, isResource),
    [projectId]
  );
  const nodeData = useReduxSelector(nodeDataSelector);
  return React.useCallback(() => {
    if (!diagram || !nodeData) {
      return;
    }

    const fields = nodeData.fields.reduce<Record<string, MappingField>>(
      (acc, { label, value }) => {
        acc[value] = { label, value, targets: [] };
        return acc;
      },
      {}
    );
    dispatch(
      uiSlice.actions.toggledCreateMappingDialog({
        isOpen: true,
        fromId: nodeData.value,
        fields,
      })
    );
  }, [diagram, dispatch, nodeData]);
};
